import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/base/MovieCard';
import { useSeo } from '@/hooks/useSeo';
import { allMovies, getHotMovies, queryMovies, sortMovies } from '@/utils/movies';

const typeTabs = [
  { value: 'all', label: '全部' },
  { value: 'movie', label: '电影' },
  { value: 'tv', label: '电视剧' },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [keyword, setKeyword] = useState(query);
  const [activeType, setActiveType] = useState('all');

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useSeo({
    title: query ? `“${query}” 的搜索结果 - 香蕉视频app` : '影视搜索 - 香蕉视频app',
    description: '在香蕉视频app 搜索电影、电视剧、演员与导演，快速找到想看的高清影视资源。',
    keywords: '影视搜索,香蕉视频app,电影搜索,电视剧搜索,高清影视',
  });

  const matched = useMemo(() => {
    const list = query ? queryMovies(allMovies, query) : [];
    const byType = activeType === 'all' ? list : list.filter((movie) => movie.type === activeType);
    return sortMovies(byType, 'latest');
  }, [query, activeType]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    const next = new URLSearchParams();
    if (q) next.set('q', q);
    setSearchParams(next, { replace: true });
  };

  const recommendations = getHotMovies(4);

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-8 pt-8 md:pt-12">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-300">搜索</span>
        </nav>

        <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-50">
          全站搜索
        </h1>

        <form
          onSubmit={handleSubmit}
          className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-2xl"
        >
          <div className="flex items-center flex-1 bg-background-100 border border-background-300 rounded-full pl-4 pr-1 h-12 focus-within:border-primary-400 transition-colors">
            <i className="ri-search-line text-foreground-400 text-lg"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="输入影片名称、演员或导演，如：科幻、星际"
              aria-label="搜索关键词"
              className="flex-1 min-w-0 bg-transparent text-sm md:text-base text-foreground-100 placeholder:text-foreground-500 px-3 outline-none"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-line"></i>
            搜索
          </button>
        </form>

        {query ? (
          <p className="mt-4 text-sm text-foreground-400">
            关键词「<span className="text-primary-400 font-semibold">{query}</span>」，共找到
            <span className="text-primary-400 font-semibold"> {matched.length} </span>个结果
          </p>
        ) : null}
      </section>

      {query ? (
        <section className="w-full px-4 md:px-8 py-8 md:py-10">
          <div className="flex items-center gap-2 p-1 rounded-full bg-background-100 border border-background-300 w-fit">
            {typeTabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveType(tab.value)}
                className={`px-4 md:px-5 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  activeType === tab.value
                    ? 'bg-primary-500 text-background-50 font-semibold'
                    : 'text-foreground-300 hover:text-primary-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {matched.length > 0 ? (
            <div data-product-shop className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {matched.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="mt-6 py-16 text-center rounded-lg border border-background-200 bg-background-100">
              <i className="ri-emotion-sad-line text-4xl text-foreground-600"></i>
              <p className="mt-4 text-foreground-100 font-medium">没有找到与「{query}」相关的影视</p>
              <p className="mt-2 text-sm text-foreground-500">可以换个关键词，或看看下面的热门推荐</p>
            </div>
          )}

          {(matched.length === 0 || matched.length < 4) ? (
            <div className="mt-12">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
                <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-50">热门推荐</h2>
              </div>
              <div data-product-shop className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {recommendations.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          ) : null}
        </section>
      ) : (
        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
            <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-50">大家都在搜</h2>
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3 mb-10">
            {['星际', '悬疑', '古装', '治愈', '科幻', '武侠', '爱情', '动作'].map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => {
                  const next = new URLSearchParams();
                  next.set('q', word);
                  setSearchParams(next, { replace: true });
                }}
                className="px-4 py-2 rounded-full text-sm text-foreground-200 bg-background-100 border border-background-300 hover:border-primary-400 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                {word}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-5">
            <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
            <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-50">热门影视</h2>
          </div>
          <div data-product-shop className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {getHotMovies(8).map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}